import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineChatBubbleOutline } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import Loader from 'components/Loader';
import Tabs from 'components/Tabs';
import { useFillAppSchema } from 'hooks/useFillAppSchema';
import EssaysSection from '../Essays/EssaysSection';
import CollegesSection from './CollegesSection';
import type { CollegeChoice } from 'global/colleges';
import {
    type FillAppFieldComment,
    type FillAppValues,
    type SectionStatus,
    buildQidLocationMap,
    computeOverallProgress,
    computeScreenProgress,
    screenRequiredComplete,
    subtabIsVisible,
    subtabRequiredComplete,
} from 'global/fillapp';

import './index.css';

interface FillAppResponse {
    id: number;
    isEnabled: boolean;
    cycleYear: number;
    essayCount: number;   // personal statement + supplements, for the hero stat
    values: FillAppValues;
    sectionStatus: SectionStatus;
    colleges: CollegeChoice[];
    collegeChecklist: Record<string, boolean>;
    fieldComments: FillAppFieldComment[];
    lastSavedAt: string | null;
}

const TAB_KEYS = ['commonapp', 'essays', 'colleges'];

// Hero progress ring geometry. r=52 with a 14px stroke puts the outer edge at
// 59 inside the 120-unit viewBox, so the rounded caps never clip.
const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

function FillApp() {
    const navigate = useNavigate();
    const { organization } = useAppSelector(state => state.organization);

    const [fillApp, setFillApp] = useState<FillAppResponse | null>(null);
    // Follow the student's own cycle once the payload lands; the hook's
    // default covers the first render, and refetches when it changes.
    const { schema, loading: schemaLoading, error: schemaError } =
        useFillAppSchema(fillApp?.cycleYear);
    // College picks live here (not in CollegesSection) so the hero stat stays
    // in sync as the student adds/removes colleges.
    const [collegeChoices, setCollegeChoices] = useState<CollegeChoice[]>([]);
    // "What you'll need" checks — lifted here (like the choices) so tab
    // switches don't lose state between CollegesSection mounts.
    const [collegeChecklist, setCollegeChecklist] = useState<Record<string, boolean>>({});
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Active tab rides in ?tab= so refresh and back-navigation keep the place.
    const [searchParams, setSearchParams] = useSearchParams();
    const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'commonapp'));
    const selectTab = (i: number) => {
        setSearchParams(i === 0 ? {} : { tab: TAB_KEYS[i] }, { replace: true });
    };

    useEffect(() => {
        if (!organization.urlSlug) return;
        let cancelled = false;
        axios.get(`fillapp/student/${organization.urlSlug}/`)
            .then(res => {
                if (cancelled) return;
                setFillApp(res.data.fillApp);
                setCollegeChoices(res.data.fillApp?.colleges || []);
                setCollegeChecklist(res.data.fillApp?.collegeChecklist || {});
                setLoaded(true);
            })
            .catch(err => {
                if (cancelled) return;
                setLoadError(err?.response?.data?.message || 'Failed to load College Apps.');
                setLoaded(true);
            });
        return () => { cancelled = true; };
    }, [organization.urlSlug]);

    // Open (unresolved) tutor comments per screen, for the section-card counts.
    // Unresolved only, matching the badges in FillAppSection's sidebar — if this
    // counted every comment, a student who had resolved them all would see a
    // number here and nothing there, and it could never fall.
    // Declared before the early returns below so the hook order stays stable.
    const openCommentsByScreen = useMemo(() => {
        const byScreen: Record<string, number> = {};
        if (!schema) return byScreen;
        const location = buildQidLocationMap(schema);
        for (const comment of fillApp?.fieldComments || []) {
            if (comment.isResolved) continue;
            const at = location.get(comment.qid);
            if (!at) continue;   // comment on a qid this cycle's schema dropped
            byScreen[at.screenId] = (byScreen[at.screenId] || 0) + 1;
        }
        return byScreen;
    }, [schema, fillApp?.fieldComments]);

    if (schemaLoading || !schema || !loaded) return <Loader />;
    if (schemaError) return <div className='layout'><div className='fappDash__error'>{schemaError}</div></div>;
    if (loadError) return <div className='layout'><div className='fappDash__error'>{loadError}</div></div>;

    if (!fillApp) {
        return (
            <div className='fappDash layout'>
                <Helmet><title>College Apps - {organization.name}</title></Helmet>
                <div className='fappDash__empty'>
                    <h1>College Apps is not yet enabled</h1>
                    <p>Ask your tutor to enable College Apps for your account to start practicing the Common Application.</p>
                </div>
            </div>
        );
    }

    const values = fillApp.values || {};
    const overall = computeOverallProgress(schema, values);
    const cycleYear = fillApp.cycleYear;

    return (
        <div className='fappDash layout'>
            <Helmet><title>College Apps - {organization.name}</title></Helmet>

            <section className='fappDash__hero'>
                <div className='fappDash__heroBody'>
                    <div className='fappDash__heroEyebrow'>
                        {cycleYear}–{cycleYear + 1} Cycle
                    </div>
                    {/* "FillApp" is the internal feature name (models, routes, the
                        customization flag) — the UI calls it College Apps everywhere. */}
                    <h1 className='fappDash__heroTitle'>Your College Apps</h1>
                    <p className='fappDash__heroSubtitle'>
                        Practice your Common Application, draft your essays, and build
                        your college list — your tutor can review everything in their portal.
                    </p>
                    <div className='fappDash__heroStats'>
                        <div className='fappDash__heroStat'>
                            <div className='fappDash__heroStatValue'>{fillApp?.essayCount ?? 0}</div>
                            <div className='fappDash__heroStatLabel'>Essays started</div>
                        </div>
                        <div className='fappDash__heroStat'>
                            <div className='fappDash__heroStatValue'>{collegeChoices.length}</div>
                            <div className='fappDash__heroStatLabel'>Colleges listed</div>
                        </div>
                        <div className='fappDash__heroStat'>
                            <div className='fappDash__heroStatValue'>{overall.percent}%</div>
                            <div className='fappDash__heroStatLabel'>Common App complete</div>
                        </div>
                    </div>
                </div>
                <div className='fappDash__heroRing'>
                    {/* SVG rather than a conic-gradient: only a stroke can carry
                        stroke-linecap, and a conic gradient's arc always ends in a
                        hard radial edge. RING_R is shared with the CSS stroke-width
                        (14) — the two have to stay in step or the ring clips. */}
                    <svg className='fappDash__heroRingSvg' viewBox='0 0 120 120' aria-hidden='true'>
                        <circle className='fappDash__heroRingTrack' cx='60' cy='60' r={RING_R} />
                        {overall.percent > 0 && (
                            <circle
                                className='fappDash__heroRingFill'
                                cx='60'
                                cy='60'
                                r={RING_R}
                                strokeDasharray={RING_C}
                                strokeDashoffset={RING_C * (1 - Math.min(overall.percent, 100) / 100)}
                            />
                        )}
                    </svg>
                    <div className='fappDash__heroRingInner'>{overall.percent}%</div>
                </div>
            </section>

            <Tabs
                className='fappDash__tabs'
                tabs={['Common App', 'Essays', 'My Colleges']}
                tabIndex={tabIndex}
                onSelect={TAB_KEYS.map((_k, i) => () => selectTab(i))}
            />

            {tabIndex === 1 && (
                <section className='fappDash__essays'>
                    <EssaysSection variant='embed' />
                </section>
            )}

            {tabIndex === 2 && (
                <CollegesSection
                    fillAppId={fillApp.id}
                    cycleYear={cycleYear}
                    choices={collegeChoices}
                    onChange={setCollegeChoices}
                    checklist={collegeChecklist}
                    onChecklistChange={setCollegeChecklist}
                />
            )}

            {tabIndex === 0 && (
            <section className='fappDash__commonApp'>
                <div className='fappDash__sectionHead'>
                    <h2>Common Application</h2>
                    <span>{schema.screens.length} sections · click one to start or continue</span>
                </div>

                <div className='fappDash__disclaimer'>
                    <strong>Practice version.</strong> College Apps is not affiliated with, endorsed by, or sponsored by The Common Application, Inc.
                    To submit a real application, you must use the official Common App at <a href='https://www.commonapp.org' target='_blank' rel='noreferrer'>commonapp.org</a>.
                </div>

                <div className='fappDash__grid'>
                {schema.screens.map(screen => {
                    const { filled, total, percent } = computeScreenProgress(screen, values);
                    const isComplete = screenRequiredComplete(screen, values);
                    const visibleSubtabs = screen.subtabs.filter(st => subtabIsVisible(st, values));
                    const firstSubtabId = visibleSubtabs[0]?.id;
                    const openComments = openCommentsByScreen[screen.id] || 0;
                    const subtabsDone = visibleSubtabs
                        .filter(st => subtabRequiredComplete(st, values)).length;
                    // Name only the first few sub-tabs; the rest become "+N more"
                    // so the line stays a hint at what's inside rather than a
                    // full table of contents.
                    const namedSubtabs = visibleSubtabs.slice(0, 3).map(st => st.label);
                    const restCount = visibleSubtabs.length - namedSubtabs.length;
                    return (
                        <button
                            key={screen.id}
                            type='button'
                            className='fappDash__tile'
                            onClick={() => firstSubtabId && navigate(`/college-apps/${screen.id}/${firstSubtabId}`)}
                        >
                            <div className='fappDash__tileTop'>
                                <h3 className='fappDash__tileTitle'>{screen.label}</h3>
                                <div className='fappDash__tileTags'>
                                    {isComplete && (
                                        <span className='fappDash__tileBadge'>Complete</span>
                                    )}
                                    {openComments > 0 && (
                                        <span
                                            className='fappDash__tileComments'
                                            title={`${openComments} unresolved tutor comment${openComments === 1 ? '' : 's'}`}
                                        >
                                            <MdOutlineChatBubbleOutline size={12} /> {openComments}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <p className='fappDash__tileDesc'>
                                {namedSubtabs.join(' · ')}
                                {restCount > 0 && ` · +${restCount} more`}
                            </p>
                            <div className='fappDash__tileProgressRow'>
                                <div className='fappDash__tileBar'>
                                    <div className='fappDash__tileBarFill' style={{ width: `${percent}%` }} />
                                </div>
                                <div className='fappDash__tileStat'>{filled}/{total} · {percent}%</div>
                            </div>
                            <div className='fappDash__tileMeta'>
                                <span>
                                    <b>{subtabsDone}</b>/{visibleSubtabs.length} sub-tabs done
                                </span>
                            </div>
                        </button>
                    );
                })}
                </div>
            </section>
            )}
        </div>
    );
}

export default FillApp;
